import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react'
import {
  Image,
  ImagePropsBase,
  ImageStyle,
  LayoutChangeEvent,
  LayoutRectangle,
  PixelRatio,
  StyleProp,
  View,
} from 'react-native'
import { useFragment, graphql } from 'react-relay'

import { selectBreakpointToken } from './util/selectBreakpointToken'
import { Element } from './Element'
import { TokensContext } from './context'
import { TokenValues } from './types'
import { useIsMounted } from './useIsMounted'
import { useBreakpoint } from './useBreakpoint'

import { GraphQLImage_image$key } from './__generated__/GraphQLImage_image.graphql'

export type TransformImageProps = {
  as?: ComponentProps<typeof Element>['as']
  children?: ReactNode
  component?: string
  onLoad?: ImagePropsBase['onLoad']
  variant?: string
  style?: StyleProp<Image>
} & UseImageTransformProps


type GraphQLImageProps = {
  image: GraphQLImage_image$key
} & Omit<TransformImageProps, 'imageUrl'>

type ImageDimensions = number | 'max'

type UseImageTransformProps = {
  borderRadius?: number | 'max'
  imageUrl: string
  maxHeight?: ImageDimensions | ImageDimensions[]
  maxWidth?: ImageDimensions | ImageDimensions[]
  focus?: 'auto' | 'face'
  transformations?: Record<string, string>
}
type CreateImageTransformURLOptions = {
  borderRadius?: number | 'max'
  focus?: 'auto' | 'face'
  layoutDimensions?: LayoutRectangle
  maxHeight?: number | 'max'
  maxWidth?: number | 'max'
  transformations?: Record<string, string | number>
}

function useImageTransform(props: UseImageTransformProps) {
  const { borderRadius, focus, imageUrl, transformations } = props

  const tokens = useContext(TokensContext)
  const [layoutDimensions, setLayoutDimensions] = useState<LayoutRectangle>()
  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const isMounted = useIsMounted()
  const breakpoint = useBreakpoint()

  const maxHeight = selectBreakpointToken(breakpoint, props.maxHeight)
  const maxWidth = selectBreakpointToken(breakpoint, props.maxWidth)

  const transformedURL = useMemo(
    () =>
      createImageTransformURL(imageUrl, tokens, {
        borderRadius,
        focus,
        layoutDimensions,
        maxHeight,
        maxWidth,
        transformations,
      }),
    [
      borderRadius,
      focus,
      imageUrl,
      layoutDimensions,
      maxHeight,
      maxWidth,
      tokens,
      transformations,
    ]
  )

  const styles = useMemo(() => {
    const appliedStyles: StyleProp<ImageStyle> = {}

    if (maxWidth === 'max') {
      appliedStyles.width = '100%'
    } else if (typeof maxWidth === 'number') {
      appliedStyles.width = (tokens.width as number[])[maxWidth]
    } else if (imageDimensions?.width) {
      appliedStyles.width = Math.ceil(imageDimensions.width / PixelRatio.get())
    }

    if (maxHeight === 'max') {
      appliedStyles.height = '100%'
    } else if (typeof maxHeight === 'number') {
      appliedStyles.height = (tokens.height as number[])[maxHeight]
    } else if (
      imageDimensions?.height &&
      !(transformations?.ar && appliedStyles.width)
    ) {
      appliedStyles.height = Math.ceil(
        imageDimensions.height / PixelRatio.get()
      )
    }

    if (maxWidth) {
      if (maxWidth === 'max') {
        appliedStyles.maxWidth = '100%'
      } else {
        appliedStyles.maxWidth = (tokens.width as number[])[maxWidth]
      }
    }

    if (maxHeight) {
      if (maxHeight === 'max') {
        appliedStyles.maxHeight = '100%'
      } else {
        appliedStyles.maxHeight = (tokens.height as number[])[maxHeight]
      }
    }

    if (borderRadius) {
      if (borderRadius === 'max') {
        appliedStyles.borderRadius = '50%' as unknown as number
      } else {
        appliedStyles.borderRadius = (tokens.borderRadius as number[])[borderRadius]
      }
    }

    if (transformations?.ar) {
      const [width, height] = transformations.ar
        .split('-')
        .map((dim) => parseInt(dim, 10))

      appliedStyles.aspectRatio = width / height
    }

    return appliedStyles
  }, [
    borderRadius,
    imageDimensions,
    maxHeight,
    maxWidth,
    tokens,
    transformations,
  ])

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (isMounted()) {
        setLayoutDimensions(event.nativeEvent.layout)
      }
    },
    [isMounted]
  )

  useEffect(() => {
    let isInEffect = true

    Image.getSize(transformedURL, (width, height) => {
      if (isInEffect) {
        setImageDimensions({ width, height })
      }
    })

    return () => {
      isInEffect = false
    }
  }, [transformedURL, setImageDimensions])

  return {
    handleLayout,
    transformedURL,
    styles,
  }
}

/**
 * For rendering images with URL string and optional transformations
 */
export const TransformImage: FC<TransformImageProps> = (props) => {
  const { as, children, component, onLoad, variant, style, ...transformProps } =
    props

  const { handleLayout, transformedURL, styles } = useImageTransform(transformProps)

  return (
    <View onLayout={handleLayout}>
      <Element
        as={as ?? Image}
        children={children}
        component={component || 'GraphQLImage'}
        onLoad={onLoad}
        source={{ uri: transformedURL }}
        style={styles}
        variant={variant}
      />
    </View>
  )
}

/**
 * For rendering images from GraphQL with optional transformations
 */
export const GraphQLImage: FC<GraphQLImageProps> = (props) => {
  const { image, ...transformImageProps } = props
  const data = useFragment(
    graphql`
      fragment GraphQLImage_image on Image {
        url
      }
    `,
    image
  )

  return <TransformImage imageUrl={data.url} {...transformImageProps} />
}


const createImageTransformURL = (
  baseURL: string,
  tokens: TokenValues,
  options: CreateImageTransformURLOptions = {}
): string => {
  const params = new URLSearchParams()

  if (options.focus) {
    params.set('fo', options.focus)
  }

  if (options.maxWidth) {
    if (options.maxWidth === 'max') {
      if (options.layoutDimensions?.width) {
        params.set(
          'w',
          String(
            PixelRatio.getPixelSizeForLayoutSize(
              Math.ceil(options.layoutDimensions?.width)
            )
          )
        )
      }
    } else {
      params.set(
        'w',
        String(
          PixelRatio.getPixelSizeForLayoutSize((tokens.width as number[])?.[options.maxWidth])
        )
      )
    }
  }

  if (options.maxHeight) {
    if (options.maxHeight === 'max') {
      if (options.layoutDimensions?.height) {
        params.set(
          'h',
          String(
            PixelRatio.getPixelSizeForLayoutSize(
              Math.ceil(options.layoutDimensions?.height)
            )
          )
        )
      }
    } else {
      params.set(
        'h',
        String(
          PixelRatio.getPixelSizeForLayoutSize((tokens.height as number[])[options.maxHeight])
        )
      )
    }
  }

  // Note: borderRadius is a CSS property, not an image transformation
  // The image worker doesn't support border-radius transforms, so we skip it here
  // Border radius is handled via CSS styles in the component instead

  const queryString = params.toString()
  if (queryString) {
    return `${baseURL}?${queryString}`
  }

  return baseURL
}
